import { Modal, Button, Label, Textarea } from "flowbite-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { KbIssue } from "@/sharedTypes";
import { useGetPaginatedCategoriesQuery } from "@/api/ticketApi/category";
import { useGetSubcategoriesListQuery } from "@/api/ticketApi/subcategory";
import PaginatedSelect from "@/components/PaginatedSelect";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<KbIssue>) => void;
  mode: "create" | "edit";
  initialData?: KbIssue | null;
  vectorLanguage?: "en" | "tr";
}

export default function KbIssueFormDialog({
  open,
  onClose,
  onSubmit,
  mode,
  initialData,
  vectorLanguage,
}: Props) {
  const { t } = useTranslation(["knowledgeBase", "common"]);
  const [formData, setFormData] = useState<Partial<KbIssue>>({
    kb_issue_text: "",
    category_id: 0,
    subcategory_id: 0,
  });

  // Category pagination & search
  const [categoryPage, setCategoryPage] = useState(1);
  const [categorySearch, setCategorySearch] = useState("");

  // Subcategory pagination & search
  const [subcategoryPage, setSubcategoryPage] = useState(1);
  const [subcategorySearch, setSubcategorySearch] = useState("");

  // Remember the labels of the chosen options so the field doesn't fall back
  // to "#id" once the current page of API results no longer contains them
  // (e.g. after the search input is cleared and page 1 is refetched).
  const [selectedCategoryLabel, setSelectedCategoryLabel] = useState<string | null>(null);
  const [selectedSubcategoryLabel, setSelectedSubcategoryLabel] = useState<string | null>(null);

  const { data: categoriesData, isFetching: isCategoriesFetching } =
    useGetPaginatedCategoriesQuery({
      pagination: { page: categoryPage, pageSize: 10 },
      filters: { "category_name[like]": categorySearch },
    });

  const { data: subcategoriesData, isFetching: isSubcategoriesFetching } =
    useGetSubcategoriesListQuery(
      {
        pagination: { page: subcategoryPage, pageSize: 10 },
        filters: {
          "subcategory_name[like]": subcategorySearch,
          category_id: formData.category_id || undefined,
        },
      },
      { skip: !formData.category_id }
    );

  const categories = categoriesData?.items || [];
  const subcategories = subcategoriesData?.items || [];

  useEffect(() => {
    if (mode === "edit" && initialData) {
      setFormData({
        kb_issue_text: initialData.kb_issue_text,
        category_id: initialData.category_id,
        subcategory_id: initialData.subcategory_id,
      });
      setSelectedCategoryLabel(initialData.category?.category_name ?? null);
      setSelectedSubcategoryLabel(initialData.subcategory?.subcategory_name ?? null);
    } else {
      setFormData({
        kb_issue_text: "",
        category_id: 0,
        subcategory_id: 0,
      });
      setSelectedCategoryLabel(null);
      setSelectedSubcategoryLabel(null);
    }
    setCategoryPage(1);
    setCategorySearch("");
    setSubcategoryPage(1);
    setSubcategorySearch("");
  }, [mode, initialData, open]);

  const getCategoryOption = () => {
    if (!formData.category_id) return null;
    if (selectedCategoryLabel) {
      return { value: formData.category_id, label: selectedCategoryLabel };
    }
    const item = categories.find(
      (c: any) => c.category_id?.toString() === formData.category_id?.toString()
    );
    if (item) return { value: item.category_id, label: item.category_name };
    if (initialData?.category && initialData.category_id === formData.category_id) {
      return { value: initialData.category_id, label: initialData.category.category_name };
    }
    return { value: formData.category_id, label: `#${formData.category_id}` };
  };

  const getSubcategoryOption = () => {
    if (!formData.subcategory_id) return null;
    if (selectedSubcategoryLabel) {
      return { value: formData.subcategory_id, label: selectedSubcategoryLabel };
    }
    const item = subcategories.find(
      (sc: any) => sc.subcategory_id?.toString() === formData.subcategory_id?.toString()
    );
    if (item) return { value: item.subcategory_id, label: item.subcategory_name };
    if (initialData?.subcategory && initialData.subcategory_id === formData.subcategory_id) {
      return { value: initialData.subcategory_id, label: initialData.subcategory.subcategory_name };
    }
    return { value: formData.subcategory_id, label: `#${formData.subcategory_id}` };
  };

  const handleSubmit = () => {
    onSubmit(formData);
  };

  return (
    <Modal show={open} onClose={onClose} size="lg">
      <Modal.Header>
        {mode === "create"
          ? t("knowledgeBase:createIssue")
          : t("knowledgeBase:editIssue")}
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          {vectorLanguage && (
            <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300">
              <span className="font-medium">{t("knowledgeBase:milvusLanguageLabel")}:</span>
              <span className="rounded bg-blue-100 px-1.5 py-0.5 font-mono text-xs font-semibold uppercase dark:bg-blue-900">
                {vectorLanguage === "en" ? "EN — English" : "TR — Türkçe"}
              </span>
            </div>
          )}
          <PaginatedSelect
            id="category_id"
            label={t("knowledgeBase:category")}
            value={getCategoryOption()}
            onChange={(option: any) => {
              const newCategoryId = option?.value ? Number(option.value) : 0;
              setFormData({
                ...formData,
                category_id: newCategoryId,
                subcategory_id: 0,
              });
              setSelectedCategoryLabel(option?.label ?? null);
              setSelectedSubcategoryLabel(null);
              setSubcategoryPage(1);
              setSubcategorySearch("");
            }}
            options={categories.map((c: any) => ({
              value: c.category_id,
              label: c.category_name,
            }))}
            onInputChange={(val) => {
              setCategorySearch(val);
              setCategoryPage(1);
            }}
            page={categoryPage}
            totalPages={categoriesData?.pagination?.totalPages || 1}
            onPageChange={setCategoryPage}
            isLoading={isCategoriesFetching}
            placeholder={t("knowledgeBase:selectCategory")}
          />

          <PaginatedSelect
            id="subcategory_id"
            label={t("knowledgeBase:subcategory")}
            value={getSubcategoryOption()}
            onChange={(option: any) => {
              setFormData({
                ...formData,
                subcategory_id: option?.value ? Number(option.value) : 0,
              });
              setSelectedSubcategoryLabel(option?.label ?? null);
            }}
            options={subcategories.map((sc: any) => ({
              value: sc.subcategory_id,
              label: sc.subcategory_name,
            }))}
            onInputChange={(val) => {
              setSubcategorySearch(val);
              setSubcategoryPage(1);
            }}
            page={subcategoryPage}
            totalPages={subcategoriesData?.pagination?.totalPages || 1}
            onPageChange={setSubcategoryPage}
            isLoading={isSubcategoriesFetching}
            placeholder={t("knowledgeBase:selectSubcategory")}
          />

          <div>
            <Label
              htmlFor="kb_issue_text"
              value={t("knowledgeBase:issueText")}
            />
            <Textarea
              id="kb_issue_text"
              rows={4}
              value={formData.kb_issue_text || ""}
              onChange={(e) =>
                setFormData({ ...formData, kb_issue_text: e.target.value })
              }
              required
            />
          </div>

        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button color="primary" onClick={handleSubmit}>
          {mode === "create"
            ? t("knowledgeBase:createIssue")
            : t("common:save")}
        </Button>
        <Button color="gray" onClick={onClose}>
          {t("common:cancel")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
